import { useEffect, useRef, useState } from 'react'
import { LogOut, MoonStar, Sun } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useBrands } from '../../contexts/BrandContext'
import { useTheme } from '../../contexts/ThemeContext'
import { initials } from '../../utils/text'
import { cn } from '@/ui/lib/utils'
import type { Session } from '../../types'

/**
 * Konsolen-UserMenu als schlankes Inline-Popover (kein Radix-Portal, damit die
 * gescopten console.css-`.menu`-Styles greifen). Trigger = `.avatar-btn` mit
 * Initialen; Content im `.menu`-Stil — `.uhead` (Name + Rollen-`.pill`), Theme-
 * Umschalter (`.mrow` mit `.switch`) und `.mrow.logout`. Schließt bei Klick
 * außerhalb und Escape. Die Auth-Info-Zeile entfällt bewusst (Clutter).
 */
export function AdminUserMenu({ session }: { session: Session }) {
  const { logout } = useAuth()
  const { resolveBrand } = useBrands()
  const { theme, toggle } = useTheme()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const brand = session.brandKey ? resolveBrand(session.brandKey) : null
  const name = session.displayName ?? session.email
  const ini = initials(session.displayName) || session.role.slice(0, 2).toUpperCase()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="usermenu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="avatar-btn"
        aria-label={`Benutzermenü — ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {ini}
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="uhead">
            <span className="av">{ini}</span>
            <div>
              <div className="nm">{name}</div>
              <div className="rl">
                <span className="pill capitalize">{session.role}</span>
                {brand && <span>{brand.brandName}</span>}
              </div>
            </div>
          </div>
          <div className="div" />
          <button type="button" role="menuitem" className="mrow" onClick={() => toggle()}>
            {theme === 'dark' ? <Sun size={17} /> : <MoonStar size={17} />}
            {theme === 'dark' ? 'Hellmodus' : 'Dunkelmodus'}
            <span className={cn('mswitch switch', theme === 'dark' && 'on')} aria-hidden />
          </button>
          <button
            type="button"
            role="menuitem"
            className="mrow logout"
            onClick={() => {
              setOpen(false)
              void logout()
            }}
          >
            <LogOut size={17} />
            Abmelden
          </button>
        </div>
      )}
    </div>
  )
}
